import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, User as UserIcon, Save, Camera, Sparkles, Edit2, Shield, Lock, Fingerprint, Trash2, Mail, Smartphone, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import ImageCropModal from '../components/ImageCropModal';
import { PINSetupDialog, useSecurityStatus } from '../components/SecurityVerification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fun Sims-style Plumbob
const Plumbob = ({ color = 'purple', size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  const colors = {
    green: ['#4ade80', '#22c55e', '#16a34a'],
    purple: ['#3A5C8F', '#2B4C7E', '#1E3A63'],
    blue: ['#60a5fa', '#3b82f6', '#2563eb']
  };
  const [c1, c2, c3] = colors[color] || colors.purple;
  
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={sizes[size]}
      animate={{ rotateY: [0, 180, 360] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      <defs>
        <linearGradient id={`plumbob-profile-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c1} />
          <stop offset="50%" stopColor={c2} />
          <stop offset="100%" stopColor={c3} />
        </linearGradient>
      </defs>
      <polygon
        points="12,2 22,12 12,22 2,12"
        fill={`url(#plumbob-profile-${color})`}
        stroke={c3}
        strokeWidth="1"
      />
    </motion.svg>
  );
};

export default function Profile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    bio: ''
  });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  
  // Image crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  
  // Security PIN state
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const { hasPin, biometricEnabled, twoFactorEnabled, twoFactorMethod, biometricLoginEnabled, loading: securityLoading } = useSecurityStatus();
  const [removingPin, setRemovingPin] = useState(false);
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [enablingBiometricLogin, setEnablingBiometricLogin] = useState(false);
  
  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setProfileData({
        bio: response.data.bio || ''
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    const token = localStorage.getItem('session_token');
    setSaving(true);

    try {
      await axios.put(
        `${BACKEND_URL}/api/users/profile`,
        profileData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: profileData
        }
      );
      toast.success('✨ Profile updated successfully!');
      loadProfile();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Create object URL for cropping
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setCropModalOpen(true);
  };

  const handleCropComplete = async (croppedBlob) => {
    const token = localStorage.getItem('session_token');
    setUploadingPicture(true);

    try {
      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile.jpg');

      await axios.post(
        `${BACKEND_URL}/api/users/profile-picture`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('🎉 Profile picture updated!');
      loadProfile();
    } catch (error) {
      console.error('Failed to upload picture:', error);
      toast.error('Failed to upload picture');
    } finally {
      setUploadingPicture(false);
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
        setImageToCrop(null);
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    const token = localStorage.getItem('session_token');
    setDeleting(true);

    try {
      await axios.delete(`${BACKEND_URL}/api/account`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { 
          confirmation: deleteConfirmation,
          password: deletePassword || null
        }
      });

      toast.success('Your account has been permanently deleted');
      
      // Clear local storage and redirect
      localStorage.clear();
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="flex justify-center mb-4">
            <Plumbob color="purple" size="lg" />
          </div>
          <p className="text-gray-600 font-medium">Loading your profile...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]" data-testid="profile-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="hidden sm:block">
              <Plumbob color="purple" size="md" />
            </div>
            <h1 className="text-lg sm:text-2xl font-medium bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent truncate">
              Your Profile
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="p-8 rounded-3xl border-2 border-[#EBF0F5] shadow-xl bg-white/80 backdrop-blur-sm">
            {/* Profile Picture Section */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative group">
                {user?.profile_picture || user?.picture ? (
                  <motion.img 
                    src={user.profile_picture || user.picture} 
                    alt={user.name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-[#D7E0EA] shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  />
                ) : (
                  <motion.div 
                    className="w-32 h-32 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center border-4 border-[#D7E0EA] shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <UserIcon className="h-16 w-16 text-[#3A5C8F]" strokeWidth={1.5} />
                  </motion.div>
                )}
                
                {/* Edit overlay */}
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
                >
                  {uploadingPicture ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <Sparkles className="h-8 w-8 text-white" />
                    </motion.div>
                  ) : (
                    <div className="text-white text-center">
                      <Camera className="h-8 w-8 mx-auto mb-1" />
                      <span className="text-xs font-medium">Edit Photo</span>
                    </div>
                  )}
                </div>

                {/* Edit button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPicture}
                  className="absolute -bottom-2 -right-2 w-10 h-10 bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full flex items-center justify-center shadow-lg hover:from-[#2B4C7E] hover:to-[#B3956B] transition-all disabled:opacity-50"
                  title="Change picture"
                >
                  <Edit2 className="h-4 w-4" />
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="picture-upload-input"
                />
              </div>
              
              <div className="text-center mt-4">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent">
                  {user?.name}
                </h2>
                <p className="text-gray-500">{user?.email}</p>
              </div>
            </div>

            {/* Bio Section */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#2B4C7E]" />
                  About You
                </label>
                <Textarea
                  placeholder="Write a brief bio that will be shared with your beneficiaries... Tell them about yourself, your life, your wishes..."
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  rows={6}
                  className="rounded-2xl border-2 border-[#D7E0EA] focus:border-[#3A5C8F] resize-none"
                  data-testid="profile-bio-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  This bio will be visible to your beneficiaries after Easy Passing is triggered.
                </p>
              </div>

              <Button 
                onClick={saveProfile} 
                disabled={saving}
                className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full hover:from-[#2B4C7E] hover:to-[#B3956B] py-6 shadow-lg shadow-[#D7E0EA]"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="flex items-center gap-2"
                  >
                    <Sparkles className="h-5 w-5" />
                    Saving...
                  </motion.div>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    Save Profile
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Security Settings Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-8 rounded-3xl border-2 border-[#EAD9BF] bg-gradient-to-br from-[#F7F1E8] to-[#F7F1E8] shadow-xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-[#D0B384] to-[#C8A97E] rounded-2xl flex items-center justify-center shadow-lg">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Security Settings</h3>
                  <p className="text-sm text-gray-600">Protect sensitive actions with PIN or biometric</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* PIN Status */}
                <div className="flex items-center justify-between p-4 bg-white/60 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <Lock className={`h-5 w-5 ${hasPin ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-medium text-gray-800">Security PIN</p>
                      <p className="text-xs text-gray-500">
                        {hasPin ? 'PIN is set up' : 'Protect finances, beneficiaries, will, and insurance'}
                      </p>
                    </div>
                  </div>
                  {!securityLoading && (
                    hasPin ? (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPinSetupOpen(true)}
                          className="rounded-full text-[#B3956B] border-[#DCC39A] hover:bg-[#F7F1E8]"
                        >
                          Change PIN
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            setRemovingPin(true);
                            const token = localStorage.getItem('session_token');
                            try {
                              await axios.delete(`${BACKEND_URL}/api/security/pin`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Security PIN removed');
                              window.location.reload();
                            } catch (error) {
                              toast.error('Failed to remove PIN');
                            } finally {
                              setRemovingPin(false);
                            }
                          }}
                          disabled={removingPin}
                          className="rounded-full text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setPinSetupOpen(true)}
                        className="bg-gradient-to-r from-[#C8A97E] to-[#C8A97E] text-white rounded-full hover:opacity-90"
                      >
                        Set Up PIN
                      </Button>
                    )
                  )}
                </div>

                {/* Biometric for In-App Security */}
                <div className="flex items-center justify-between p-4 bg-white/60 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <Fingerprint className={`h-5 w-5 ${biometricEnabled ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-medium text-gray-800">Face ID / Touch ID (In-App)</p>
                      <p className="text-xs text-gray-500">
                        {biometricEnabled ? 'Biometric verification enabled' : 'Use biometric for in-app PIN verification'}
                      </p>
                    </div>
                  </div>
                  {hasPin && !securityLoading && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const token = localStorage.getItem('session_token');
                        try {
                          await axios.post(`${BACKEND_URL}/api/security/enable-biometric`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Biometric authentication enabled');
                          window.location.reload();
                        } catch (error) {
                          toast.error('Failed to enable biometric');
                        }
                      }}
                      disabled={biometricEnabled}
                      className="rounded-full text-[#2B4C7E] border-[#B9C7D6] hover:bg-[#EBF0F5]"
                    >
                      {biometricEnabled ? 'Enabled' : 'Enable'}
                    </Button>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t-2 border-[#EAD9BF] my-4" />
                <p className="text-sm font-medium text-[#9C7F55] mb-3">🔑 Login Security</p>

                {/* Two-Factor Authentication */}
                <div className="flex items-center justify-between p-4 bg-white/60 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <Mail className={`h-5 w-5 ${twoFactorEnabled ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-medium text-gray-800">Two-Step Verification</p>
                      <p className="text-xs text-gray-500">
                        {twoFactorEnabled 
                          ? `Enabled via ${twoFactorMethod === 'email' ? 'Email' : 'Authenticator App'}` 
                          : 'Require code at login for extra security'}
                      </p>
                    </div>
                  </div>
                  {!securityLoading && (
                    twoFactorEnabled ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          const token = localStorage.getItem('session_token');
                          try {
                            await axios.delete(`${BACKEND_URL}/api/auth/2fa`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Two-step verification disabled');
                            window.location.reload();
                          } catch (error) {
                            toast.error('Failed to disable 2FA');
                          }
                        }}
                        className="rounded-full text-red-500 hover:bg-red-50"
                      >
                        Disable
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          setEnabling2FA(true);
                          const token = localStorage.getItem('session_token');
                          try {
                            await axios.post(`${BACKEND_URL}/api/auth/2fa/setup`, 
                              { method: 'email' },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success('Two-step verification enabled! You will receive a code via email at each login.');
                            window.location.reload();
                          } catch (error) {
                            toast.error('Failed to enable 2FA');
                          } finally {
                            setEnabling2FA(false);
                          }
                        }}
                        disabled={enabling2FA}
                        className="rounded-full text-[#2B4C7E] border-[#B9C7D6] hover:bg-[#EBF0F5]"
                      >
                        {enabling2FA ? 'Enabling...' : 'Enable'}
                      </Button>
                    )
                  )}
                </div>

                {/* Biometric Login */}
                <div className="flex items-center justify-between p-4 bg-white/60 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <Smartphone className={`h-5 w-5 ${biometricLoginEnabled ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-medium text-gray-800">Face ID / Touch ID Login</p>
                      <p className="text-xs text-gray-500">
                        {biometricLoginEnabled 
                          ? 'Sign in with biometrics instead of password' 
                          : 'Use Face ID or Touch ID to sign in'}
                      </p>
                    </div>
                  </div>
                  {!securityLoading && (
                    biometricLoginEnabled ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          const token = localStorage.getItem('session_token');
                          try {
                            await axios.delete(`${BACKEND_URL}/api/auth/biometric`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Biometric login disabled');
                            window.location.reload();
                          } catch (error) {
                            toast.error('Failed to disable biometric login');
                          }
                        }}
                        className="rounded-full text-red-500 hover:bg-red-50"
                      >
                        Disable
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          setEnablingBiometricLogin(true);
                          try {
                            // Check if platform authenticator is available
                            if (!window.PublicKeyCredential) {
                              toast.error('Biometric login not supported on this device');
                              setEnablingBiometricLogin(false);
                              return;
                            }
                            
                            const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                            if (!available) {
                              toast.error('No biometric authenticator found on this device');
                              setEnablingBiometricLogin(false);
                              return;
                            }
                            
                            // Create credential
                            const challenge = new Uint8Array(32);
                            crypto.getRandomValues(challenge);
                            
                            const userId = new TextEncoder().encode(user?.user_id || 'user');
                            
                            const credential = await navigator.credentials.create({
                              publicKey: {
                                challenge,
                                rp: { name: 'Easy Passing', id: window.location.hostname },
                                user: {
                                  id: userId,
                                  name: user?.email || 'user@example.com',
                                  displayName: user?.name || 'User'
                                },
                                pubKeyCredParams: [
                                  { alg: -7, type: 'public-key' },  // ES256
                                  { alg: -257, type: 'public-key' } // RS256
                                ],
                                authenticatorSelection: {
                                  authenticatorAttachment: 'platform',
                                  userVerification: 'required',
                                  residentKey: 'required'
                                },
                                timeout: 60000
                              }
                            });
                            
                            if (credential) {
                              const token = localStorage.getItem('session_token');
                              
                              // Convert credential ID to base64
                              const credentialIdArray = new Uint8Array(credential.rawId);
                              const credentialIdBase64 = btoa(String.fromCharCode(...credentialIdArray));
                              
                              // Store credential ID in localStorage for login
                              localStorage.setItem('biometric_credential_id', credentialIdBase64);
                              localStorage.setItem('biometric_user_email', user?.email);
                              
                              // Register credential on server
                              await axios.post(`${BACKEND_URL}/api/auth/biometric/register`, 
                                { 
                                  credential_id: credentialIdBase64,
                                  public_key: 'webauthn_credential'
                                },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              
                              // Enable biometric login
                              await axios.post(`${BACKEND_URL}/api/auth/biometric/enable`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              
                              toast.success('Biometric login enabled! You can now sign in with Face ID / Touch ID.');
                              window.location.reload();
                            }
                          } catch (error) {
                            console.error('Failed to enable biometric login:', error);
                            if (error.name === 'NotAllowedError') {
                              toast.error('Biometric authentication was cancelled');
                            } else {
                              toast.error('Failed to set up biometric login. Please try again.');
                            }
                          } finally {
                            setEnablingBiometricLogin(false);
                          }
                        }}
                        disabled={enablingBiometricLogin}
                        className="rounded-full text-green-600 border-green-300 hover:bg-green-50"
                      >
                        {enablingBiometricLogin ? 'Setting up...' : 'Enable'}
                      </Button>
                    )
                  )}
                </div>

                {/* Protected Areas Info */}
                <div className="mt-4 p-4 bg-[#F2E9D9]/50 rounded-xl">
                  <p className="text-sm font-medium text-[#836A45] mb-2">🔒 Protected Actions:</p>
                  <ul className="text-xs text-[#9C7F55] space-y-1">
                    <li>• Accessing Funds & Finance section</li>
                    <li>• Modifying beneficiary fund contributions</li>
                    <li>• Editing or deleting your Will</li>
                    <li>• Editing or deleting Insurance policies</li>
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Account Management - Delete Account */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6"
          >
            <Card className="p-6 sm:p-8 rounded-3xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-[#F7F1E8] shadow-xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-red-400 to-[#C8A97E] rounded-2xl flex items-center justify-center shadow-lg">
                  <AlertTriangle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">Account Management</h3>
                  <p className="text-sm text-gray-600">Manage your account settings</p>
                </div>
              </div>

              <div className="p-4 bg-white/60 rounded-2xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="font-medium text-gray-800">Delete Account</p>
                    <p className="text-sm text-gray-500">
                      Permanently delete your account and all data. This cannot be undone.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="rounded-full border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 whitespace-nowrap"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              </div>

              <p className="text-xs text-red-600 mt-4">
                ⚠️ Warning: Deleting your account will permanently remove all your data including beneficiaries, 
                whispers, documents, credentials vault, fund allocations, and any other information stored with Easy Passing.
              </p>
            </Card>
          </motion.div>
        </motion.div>
      </main>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl border-2 border-red-200">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <DialogTitle className="text-xl text-red-600">Delete Account</DialogTitle>
            </div>
            <DialogDescription className="text-gray-600">
              This action is <strong className="text-red-600">permanent and cannot be undone</strong>. 
              All your data will be permanently deleted including:
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-50 rounded-xl p-4 my-4">
            <ul className="text-sm text-red-700 space-y-1">
              <li>• All beneficiary information and invitations</li>
              <li>• All whispers (messages, photos, videos)</li>
              <li>• Photo albums and memories</li>
              <li>• Credentials vault entries</li>
              <li>• Will and insurance documents</li>
              <li>• Fund accounts and distributions</li>
              <li>• Obituary and funeral preferences</li>
              <li>• All security settings</li>
            </ul>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <strong className="text-red-600">DELETE</strong> to confirm
              </label>
              <Input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE"
                className="rounded-xl border-2 border-red-200 focus:border-red-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your password (optional, for verification)
              </label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Your password"
                className="rounded-xl border-2 border-gray-200 focus:border-gray-400"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteConfirmation('');
                setDeletePassword('');
              }}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== 'DELETE' || deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
            >
              {deleting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                  </motion.div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Forever
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          if (imageToCrop) {
            URL.revokeObjectURL(imageToCrop);
            setImageToCrop(null);
          }
        }}
        imageSrc={imageToCrop}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
      />

      {/* PIN Setup Dialog */}
      <PINSetupDialog
        open={pinSetupOpen}
        onClose={() => setPinSetupOpen(false)}
        onSuccess={() => window.location.reload()}
      />
    </div>
  );
}
